import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { Check, Loader2 } from "lucide-react";

const STEPS = [
  { label: "SOLICITAÇÃO ABERTA", desc: "Infiltração no banheiro — Apt 42" },
  { label: "ANÁLISE", desc: "Demanda avaliada e classificada" },
  { label: "PROFISSIONAL ACIONADO", desc: "Encanador parceiro designado" },
  { label: "EXECUÇÃO", desc: "Serviço em andamento no imóvel" },
  { label: "RESOLVIDO", desc: "Concluído, validado e registrado" },
];

export const FlowCard = () => {
  const [active, setActive] = useState(0);
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 16 });
  const sry = useSpring(ry, { stiffness: 120, damping: 16 });

  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % (STEPS.length + 1)), 1300);
    return () => clearInterval(t);
  }, []);

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    ry.set(px * 10);
    rx.set(-py * 10);
  };

  const done = active >= STEPS.length;
  const progress = Math.min(active / (STEPS.length - 1), 1);

  return (
    <div className="perspective-1200" data-testid="hero-flow-card">
      <motion.div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={() => {
          rx.set(0);
          ry.set(0);
        }}
        style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, y: 40, rotateX: 8 }}
        animate={{ opacity: 1, y: 0, rotateX: 0 }}
        transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="relative overflow-hidden rounded-3xl bg-petrol p-6 shadow-[0_40px_80px_-20px_rgba(11,25,44,0.5)] sm:p-8"
      >
        <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full glow-brand-strong" />
        <div className="relative flex items-center justify-between">
          <div>
            <p className="font-mono text-[0.65rem] font-semibold uppercase tracking-[0.22em] text-brand-400">
              Fluxo de demanda
            </p>
            <p className="mt-1 font-display text-lg font-bold text-white">Chamado #1042</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1 font-mono text-[0.65rem] uppercase tracking-widest text-emerald-400">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
            </span>
            ao vivo
          </span>
        </div>

        <div className="relative mt-7">
          <div className="absolute bottom-5 left-[13px] top-2 w-px bg-white/10" />
          <motion.div
            className="absolute left-[13px] top-2 w-px origin-top bg-brand"
            animate={{ height: `calc(${progress * 100}% - ${progress * 20}px)` }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />
          <ul className="relative space-y-5">
            {STEPS.map((step, i) => {
              const isDone = i < active || done;
              const isActive = i === active && !done;
              return (
                <li key={step.label} className="flex items-start gap-4" data-testid={`flow-step-${i}`}>
                  <span
                    className={`relative z-10 mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border transition-colors duration-500 ${
                      isDone
                        ? "border-emerald-400 bg-emerald-400/15 text-emerald-400"
                        : isActive
                          ? "border-brand bg-brand text-white"
                          : "border-white/20 bg-petrol-800 text-white/30"
                    }`}
                  >
                    {isDone ? (
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    ) : isActive ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={3} />
                    ) : (
                      <span className="h-1.5 w-1.5 rounded-full bg-current" />
                    )}
                  </span>
                  <div className="min-w-0">
                    <p
                      className={`font-mono text-xs font-semibold tracking-[0.14em] transition-colors duration-500 ${
                        isDone ? "text-emerald-400" : isActive ? "text-white" : "text-white/35"
                      }`}
                    >
                      {step.label}
                      {isDone && i === STEPS.length - 1 && " ✓"}
                    </p>
                    <p
                      className={`mt-0.5 truncate text-sm transition-colors duration-500 ${
                        isActive || isDone ? "text-white/60" : "text-white/25"
                      }`}
                    >
                      {step.desc}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="relative mt-7 flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3">
          <span className="font-mono text-[0.65rem] uppercase tracking-widest text-white/50">
            tempo médio de análise
          </span>
          <span className="font-display text-sm font-bold text-white">24h</span>
        </div>
      </motion.div>
    </div>
  );
};
